"use client";

import AuthModal from "@/components/Auth/AuthModal/page";
import PddRegisterForm from "@/components/Auth/AuthModal/PddRegisterForm";
import { useLocale } from "@/hooks/use-locale";
import { useInterfaceLanguageOptions } from "@/hooks/use-ui-language-options";
import { useTranslations } from "next-intl";
import React, { useState, useRef, useEffect } from "react";
import { Logo } from "../Logo";
import { getBrandConfig } from "@/lib/branding";

interface AuthPageProps {
  onLoginSuccess?: () => void;
  backgroundImageSrc?: string;
  topLeftLogoSrc?: string;
  pddTwoStep?: boolean;
}

const AuthPage: React.FC<AuthPageProps> = ({ onLoginSuccess, backgroundImageSrc, topLeftLogoSrc, pddTwoStep }) => {
  const t = useTranslations();
  const brand = getBrandConfig();
  const { currentLocale, changeLanguage } = useLocale();
  const languages = useInterfaceLanguageOptions();
  const [mode, setMode] = useState<"register" | "login">("login");
  const [pddStep, setPddStep] = useState<"register" | "login">("register");
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const getAuthLanguageShortLabel = (locale: string, fallback: string) => {
    return locale.toLowerCase().startsWith("fr") ? "FR" : fallback;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedData = () => setIsVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoadedData);

      // Проверка если видео уже загружено
      if (video.readyState >= 3) {
        setTimeout(() => setIsVideoLoaded(true), 0);
      }

      return () => video.removeEventListener('loadeddata', handleLoadedData);
    }
  }, []);

  const toggleMode = () => {
    setMode((prev) => (prev === "login" ? "register" : "login"));
  };

  const authContent =
    pddTwoStep && pddStep === "register" ? (
      <PddRegisterForm onDone={() => setPddStep("login")} />
    ) : (
      <AuthModal
        mode={mode}
        onToggle={toggleMode}
        onLoginSuccess={onLoginSuccess}
        pddMode={pddTwoStep}
      />
    );

  return (
    <>
      {/* ── MOBILE LAYOUT (hidden on md+) ── */}
      <div
        className="md:hidden relative min-h-dvh w-full flex flex-col"
        style={{ backgroundColor: "#0a0035" }}
      >
        {/* Video/Image background */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          {backgroundImageSrc ? (
            <img className="auth-video" src={backgroundImageSrc} alt="" style={{ opacity: 1 }} />
          ) : (
            <>
              <video
                ref={videoRef}
                className="auth-video"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedData={() => setIsVideoLoaded(true)}
                style={{ opacity: isVideoLoaded ? 1 : 0 }}
              >
                <source src="/auth-bg.mp4" type="video/mp4" />
              </video>
              {!isVideoLoaded && (
                <div
                  className="auth-video-overlay"
                  style={{ background: "linear-gradient(135deg, #0a0035 0%, #001a33 100%)" }}
                />
              )}
            </>
          )}
        </div>

        {/* Content above video */}
        <div className="relative z-10 flex flex-col items-center px-4 pt-14 pb-22 flex-1">
          {/* Logo at top */}
          <div className="mb-6 mt-4">
            <Logo variant="light" className="w-[160px] h-[auto]" />
          </div>

          {/* White form card */}
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl">
            {/* Language switcher inside card, centered */}
            <div className="flex justify-center mb-4">
              <div className="flex gap-5 items-center">
                {languages.map((lang) => (
                  <button
                    key={lang.locale}
                    onClick={() => changeLanguage(lang.locale)}
                    className={`w-9 h-9 text-xs font-medium transition-all rounded-lg flex items-center justify-center ${
                      currentLocale === lang.locale
                        ? "bg-gray-100 text-blue-600"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {getAuthLanguageShortLabel(lang.locale, lang.short)}
                  </button>
                ))}
              </div>
            </div>

            {/* Auth form */}
            {authContent}
          </div>

          {/* Branding below card */}
          <div className="mt-6 text-center px-4">
            <p
              className="uppercase text-center mb-3"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: "8px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {t("label-auth-subtitle-small")}
            </p>
            <p
              className="text-center mb-3"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "30px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {brand.displayName}
            </p>
            {/* <div className="flex mb-3 justify-center">
              <Logo variant="light" className="w-[160px] h-[auto]" />
            </div> */}
            <p
              className="text-center"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {t("label-auth-description")}
            </p>
          </div>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-center min-h-screen p-4 w-full" style={{ backgroundColor: '#4E4E4E' }}>
        <div className="flex flex-row w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div
            className={`flex flex-col justify-between relative overflow-hidden ${
              backgroundImageSrc ? "shrink-0" : "w-1/2"
            }`}
            style={{
              padding: "24px",
              margin: "12px",
              borderRadius: "13px",
              backgroundColor: "#0a0035",
              ...(backgroundImageSrc
                ? { width: "490px", height: "580px" }
                : {}),
            }}
          >
            <div className="auth-video-bg" aria-hidden="true">
              {backgroundImageSrc ? (
                <img className="auth-video" src={backgroundImageSrc} alt="" style={{ opacity: 1 }} />
              ) : (
                <>
                  <video
                    className="auth-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onLoadedData={() => setIsVideoLoaded(true)}
                    style={{ opacity: isVideoLoaded ? 1 : 0 }}
                  >
                    <source src="/auth-bg.mp4" type="video/mp4" />
                  </video>
                  {!isVideoLoaded && (
                    <div
                      className="auth-video-overlay"
                      style={{ background: "linear-gradient(135deg, #0a0035 0%, #001a33 100%)" }}
                    />
                  )}
                </>
              )}
            </div>

            {backgroundImageSrc && (
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  zIndex: 2,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.77) 100%)",
                }}
              />
            )}

            <div className="relative z-10 flex items-center justify-between gap-4">
              {topLeftLogoSrc && (
                <img src="/ai-ministry.png" alt="" className="h-18 w-auto" />
              )}
              {topLeftLogoSrc && (
                <img
                  src={topLeftLogoSrc}
                  alt=""
                  className={`h-14 w-auto ${pddTwoStep ? "cursor-pointer" : ""}`}
                  onClick={pddTwoStep ? () => setPddStep("register") : undefined}
                />
              )}
            </div>

            {!backgroundImageSrc && (
              <div className="relative z-10">
                <p className="text-sm uppercase text-white/70 mb-2" />
                <div className="relative z-10">
                  <Logo variant="light" className="w-[150px] h-[43px]" />
                </div>
                <p className="text-white/90 leading-relaxed">
                  {t("label-auth-description")}
                </p>
              </div>
            )}

            {backgroundImageSrc && (
              <div className="relative z-10 flex items-center justify-start gap-10">
                <Logo variant="light" className="h-5 w-auto" />
                <img src="/Seven.svg" alt="" className="h-8 w-auto" />
              </div>
            )}
          </div>

          {/* Right panel: form */}
          <div className="w-1/2 p-8 flex flex-col">
            <div className={`flex mb-4 ${backgroundImageSrc ? "justify-end" : "justify-center"}`}>
              <div className="flex gap-5 items-center">
                {languages.map((lang) => (
                  <button
                    key={lang.locale}
                    onClick={() => changeLanguage(lang.locale)}
                    className={`w-9 h-9 text-xs font-medium transition-all rounded-lg flex items-center justify-center ${
                      currentLocale === lang.locale
                        ? "bg-gray-100 text-blue-600"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {getAuthLanguageShortLabel(lang.locale, lang.short)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-md">
                {authContent}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-video-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border-radius: 13px;
          isolation: isolate;
          pointer-events: none;
        }

        .auth-video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 1;
          transition: opacity 0.5s ease-in;
        }

        .auth-video-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          transition: background 0.5s ease-in;
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-video {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default AuthPage;
